import React from 'react'
import {
  Alert,
  Box,
  Chip,
  Divider,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import { Link as RouterLink, Outlet, useLocation, useOutletContext } from 'react-router-dom'
import { useMe } from '../app/MeContext'
import { getIntegrations, type IntegrationProvider } from '../api/integrations'
import type { AppOutletContext } from '../app/AppShell'

type SnackbarState = {
  open: boolean
  message: string
  severity: 'success' | 'error'
}

type SettingsIntegrationsContextValue = {
  stationId: number | null
  providers: Record<string, IntegrationProvider>
  loading: boolean
  error: string | null
  refreshProviders: () => Promise<void>
  notify: (severity: 'success' | 'error', message: string) => void
}

const SettingsIntegrationsContext = React.createContext<SettingsIntegrationsContextValue | undefined>(undefined)

export const useSettingsIntegrations = (): SettingsIntegrationsContextValue => {
  const context = React.useContext(SettingsIntegrationsContext)

  if (!context) {
    throw new Error('useSettingsIntegrations must be used within SettingsIntegrationsContext provider')
  }

  return context
}

type NavItem = {
  label: string
  to: string
  providerId?: string
}

const integrationNavItems: NavItem[] = [
  { label: 'Overview', to: '/settings/integrations' },
  { label: 'Email', to: '/settings/integrations/email', providerId: 'email' },
  { label: 'Telegram', to: '/settings/integrations/telegram', providerId: 'telegram' },
]

const Settings: React.FC = () => {
  const me = useMe()
  const appContext = useOutletContext<AppOutletContext>()
  const { activeStation } = appContext
  const location = useLocation()
  const stationId = activeStation?.id ?? me.stations?.[0]?.id ?? null

  const [providers, setProviders] = React.useState<Record<string, IntegrationProvider>>({})
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [snackbar, setSnackbar] = React.useState<SnackbarState | null>(null)

  const resolveErrorMessage = React.useCallback((err: unknown, fallback: string) => {
    if (err instanceof Error) {
      return err.message
    }

    if (err && typeof err === 'object') {
      if ('message' in err) {
        return String((err as { message?: unknown }).message ?? fallback)
      }

      if ('code' in err) {
        return String((err as { code?: unknown }).code ?? fallback)
      }
    }

    return fallback
  }, [])

  const mapProviders = React.useCallback((list: IntegrationProvider[] = []) => {
    const map: Record<string, IntegrationProvider> = {}

    for (const provider of list) {
      map[provider.id] = provider
    }

    return map
  }, [])

  const refreshProviders = React.useCallback(async () => {
    if (!stationId) {
      setProviders({})
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await getIntegrations(stationId)
      setProviders(mapProviders(response.providers ?? []))
    } catch (err) {
      const message = resolveErrorMessage(err, 'Unable to load integrations.')
      setError(message)
      setProviders({})
      throw err
    } finally {
      setLoading(false)
    }
  }, [mapProviders, resolveErrorMessage, stationId])

  React.useEffect(() => {
    if (!stationId) {
      setProviders({})
      setError(null)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    getIntegrations(stationId)
      .then((response) => {
        if (cancelled) {
          return
        }
        setProviders(mapProviders(response.providers ?? []))
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        const message = resolveErrorMessage(err, 'Unable to load integrations.')
        setError(message)
        setProviders({})
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [mapProviders, resolveErrorMessage, stationId])

  const handleNotify = React.useCallback((severity: 'success' | 'error', message: string) => {
    setSnackbar({ open: true, severity, message })
  }, [])

  const handleSnackbarClose = () => {
    setSnackbar(null)
  }

  const contextValue = React.useMemo<SettingsIntegrationsContextValue>(
    () => ({ stationId, providers, loading, error, refreshProviders, notify: handleNotify }),
    [stationId, providers, loading, error, refreshProviders, handleNotify],
  )

  const activePath = location.pathname

  const primaryNavItems = React.useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
      { label: 'General', to: '/settings/general' },
      { label: 'Station', to: '/settings/station' },
      { label: 'Users', to: '/settings/users' },
      { label: 'Billing', to: '/settings/billing' },
      { label: 'Integrations', to: '/settings/integrations' },
    ]

    items.push({ label: 'Show Clock', to: '/settings/show-clock' })
    items.push({ label: 'Profile', to: '/settings/profile' })

    return items
  }, [])

  const renderPrimaryNav = () => (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {primaryNavItems.map((item) => {
        const isActive = activePath === item.to || activePath.startsWith(`${item.to}/`)
        const requiresStation = item.to.startsWith('/settings/integrations')
        const disabled = requiresStation && !stationId

        const componentProps = disabled
          ? {}
          : ({ component: RouterLink, to: item.to } as const)

        return (
          <Chip
            key={item.to}
            label={item.label}
            clickable={!disabled}
            color={isActive ? 'primary' : 'default'}
            variant={isActive ? 'filled' : 'outlined'}
            sx={{ borderRadius: 999 }}
            {...componentProps}
          />
        )
      })}
    </Stack>
  )

  const renderStatusChip = (providerId?: string) => {
    if (!providerId) {
      return null
    }

    const provider = providers[providerId]

    if (!provider) {
      if (loading) {
        return <Chip label="Loading…" size="small" />
      }
      return <Chip label="Unavailable" size="small" color="default" variant="outlined" />
    }

    switch (provider.status) {
      case 'ok':
        return <Chip label="Connected" size="small" color="success" />
      case 'corrupt':
        return <Chip label="Error" size="small" color="error" variant="outlined" />
      default:
        return <Chip label="Not configured" size="small" color="default" variant="outlined" />
    }
  }

  const onProfilePage = activePath.startsWith('/settings/profile')
  const onIntegrationsPage = activePath.startsWith('/settings/integrations')
  const hasSideCard = onIntegrationsPage

  return (
    <SettingsIntegrationsContext.Provider value={contextValue}>
      <Stack spacing={3}>
        {!onProfilePage ? (
          <Stack spacing={1}>
            <Typography variant="h4" component="h1">
              Settings
            </Typography>
            <Typography color="text.secondary">
              Station-level configuration for integrations, presenters, and automation lives here.
            </Typography>
          </Stack>
        ) : null}

        <Box>
          {renderPrimaryNav()}
        </Box>

        <Stack
          direction={hasSideCard ? { xs: 'column', md: 'row' } : 'column'}
          spacing={3}
          alignItems={hasSideCard ? { xs: 'stretch', md: 'flex-start' } : 'stretch'}
        >
          <Stack sx={{ flexGrow: 1, minWidth: 0 }} spacing={2}>
            <Outlet context={appContext} />
          </Stack>

          {onIntegrationsPage ? (
            <Paper
              variant="outlined"
              sx={{ width: { xs: '100%', md: 260 }, borderRadius: 2, overflow: 'hidden' }}
            >
              <Stack spacing={1.5} sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Integrations
                </Typography>
                <Divider />
                <List disablePadding>
                  {integrationNavItems.map((item) => {
                    const isActive =
                      activePath === item.to || activePath.startsWith(`${item.to}/`)
                    const Component = stationId ? RouterLink : 'div'

                    return (
                      <ListItemButton
                        key={item.to}
                        component={Component as React.ElementType}
                        to={stationId ? item.to : undefined}
                        disabled={!stationId}
                        selected={isActive}
                        sx={{
                          borderRadius: 1.5,
                          mb: 1,
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            '&:hover': { bgcolor: 'action.selected' },
                          },
                        }}
                      >
                        <ListItemText primary={item.label} />
                        {renderStatusChip(item.providerId)}
                      </ListItemButton>
                    )
                  })}
                </List>
                {!stationId ? <Alert severity="info">Select a station to manage integrations.</Alert> : null}
                {error ? <Alert severity="error">{error}</Alert> : null}
                {loading && stationId ? <LinearProgress sx={{ mt: 1 }} /> : null}
              </Stack>
            </Paper>
          ) : null}
        </Stack>

        <Snackbar
          open={snackbar?.open ?? false}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {snackbar ? (
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          ) : null}
        </Snackbar>
      </Stack>
    </SettingsIntegrationsContext.Provider>
  )
}

export default Settings
